import React from 'react';
import { Box, Switch, Typography, styled } from '@mui/material';

interface YesNoToggleProps {
    value: string[] | null;
    onChange: (isYes: boolean) => void;
    disabled?: boolean;
    size?: 'small' | 'medium';
    showLabels?: boolean;
}

const InlineToggleSwitch = styled(Switch)(({ theme }) => ({
    width: 70,
    height: 32,
    padding: 0,
    '& .MuiSwitch-switchBase': {
        padding: 0,
        margin: 2,
        transitionDuration: '300ms',
        '&.Mui-checked': {
            transform: 'translateX(36px)',
            color: '#fff',
            '& + .MuiSwitch-track': {
                backgroundColor: theme.palette.success.main,
                opacity: 1,
                border: 0,
            },
            '&.Mui-disabled + .MuiSwitch-track': {
                opacity: 0.5,
            },
        },
        '&.Mui-focusVisible .MuiSwitch-thumb': {
            color: theme.palette.success.main,
            border: '4px solid #fff',
        },
        '&.Mui-disabled .MuiSwitch-thumb': {
            color: theme.palette.grey[100],
        },
        '&.Mui-disabled + .MuiSwitch-track': {
            opacity: 0.3,
        },
    },
    '& .MuiSwitch-thumb': {
        boxSizing: 'border-box',
        width: 28,
        height: 28,
        backgroundColor: '#fff',
        boxShadow: '0 1px 3px 0 rgb(0 35 11 / 20%)',
    },
    '& .MuiSwitch-track': {
        borderRadius: 20,
        backgroundColor: theme.palette.error.main,
        opacity: 1,
        transition: theme.transitions.create(['background-color'], {
            duration: 500,
        }),
        position: 'relative',
        '&:before, &:after': {
            content: '""',
            position: 'absolute',
            top: '50%',
            transform: 'translateY(-50%)',
            width: 16,
            height: 16,
        },
    },
}));

const ToggleContainer = styled(Box)({
    position: 'relative',
    display: 'inline-block',
});

const LabelText = styled(Typography)(({ theme }) => ({
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    fontSize: '11px',
    fontWeight: 600,
    color: '#fff',
    pointerEvents: 'none',
    zIndex: 1,
}));

export const YesNoToggle: React.FC<YesNoToggleProps> = ({
    value = [],
    onChange,
    disabled = false,
    size = 'medium',
    showLabels = true,
}) => {
    const answerValue = Array.isArray(value) ? value : [];
    const isYesSelected = answerValue.includes('YES');
    const isNoSelected = answerValue.includes('NO');
    const toggleValue = isYesSelected ? true : (isNoSelected ? false : false);

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        onChange(event.target.checked);
    };

    return (
        <ToggleContainer>
            <LabelText
                sx={{
                    left: toggleValue ? 6 : 40,
                    transition: 'left 0.3s ease-in-out',
                }}
            >
                {toggleValue ? 'YES' : 'NO'}
            </LabelText>
            <InlineToggleSwitch
                checked={toggleValue}
                onChange={handleChange}
                disabled={disabled}
            />
        </ToggleContainer>
    );
};

export default YesNoToggle;
